import { useState } from 'react';
import { Plus, RefreshCw, Loader2 } from 'lucide-react';
import { usePatrolCheckpoints } from '../hooks';
import { CheckpointList, CheckpointForm, CheckpointQRModal } from '../components';
import { useI18n } from '@/shared/i18n';
import { useToast } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import type { PatrolCheckpoint, CreateCheckpointDTO, UpdateCheckpointDTO, CheckpointQRResponse } from '@yawi/types';

export const PatrolCheckpointsPage = () => {
  const { t } = useI18n();
  const toast = useToast();
  const ability = useAbility();
  const canManage = ability.can('manage', 'PatrolCheckpoint');

  const {
    checkpoints,
    loading,
    error,
    createCheckpoint,
    updateCheckpoint,
    deleteCheckpoint,
    getCheckpointQR,
    refresh,
  } = usePatrolCheckpoints(false); // Get all including inactive

  const [showForm, setShowForm] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState<PatrolCheckpoint | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [qrModalData, setQrModalData] = useState<{
    qrData: CheckpointQRResponse;
    checkpoint: PatrolCheckpoint;
  } | null>(null);

  const handleCreateCheckpoint = async (data: CreateCheckpointDTO) => {
    setFormLoading(true);
    try {
      await createCheckpoint(data);
      setShowForm(false);
      toast.success(t('patrol.checkpointCreated'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateCheckpoint = async (data: UpdateCheckpointDTO) => {
    if (!editingCheckpoint) return;
    setFormLoading(true);
    try {
      await updateCheckpoint(editingCheckpoint.id, data);
      setEditingCheckpoint(null);
      toast.success(t('patrol.checkpointUpdated'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (checkpoint: PatrolCheckpoint) => {
    try {
      await updateCheckpoint(checkpoint.id, { isActive: !checkpoint.isActive });
      toast.success(checkpoint.isActive ? t('patrol.checkpointDeactivated') : t('patrol.checkpointActivated'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const handleDelete = async (checkpoint: PatrolCheckpoint) => {
    try {
      await deleteCheckpoint(checkpoint.id);
      toast.success(t('patrol.checkpointDeleted'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const handleShowQR = async (checkpoint: PatrolCheckpoint) => {
    try {
      const qrData = await getCheckpointQR(checkpoint.id);
      setQrModalData({ qrData, checkpoint });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const nextOrderIndex = checkpoints.length > 0
    ? Math.max(...checkpoints.map(c => c.orderIndex)) + 1
    : 1;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">{t('patrol.checkpoints')}</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canManage && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('patrol.newCheckpoint')}
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">{t('patrol.totalCheckpoints')}</p>
          <p className="text-2xl font-bold text-slate-800">{checkpoints.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">{t('patrol.activeCheckpoints')}</p>
          <p className="text-2xl font-bold text-green-600">
            {checkpoints.filter(c => c.isActive).length}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Checkpoints List */}
      {!loading && (
        <CheckpointList
          checkpoints={checkpoints}
          onEdit={canManage ? setEditingCheckpoint : undefined}
          onToggleActive={canManage ? handleToggleActive : undefined}
          onDelete={canManage ? handleDelete : undefined}
          onShowQR={handleShowQR}
          showActions={canManage}
        />
      )}

      {/* Create Form Modal */}
      {showForm && (
        <CheckpointForm
          nextOrderIndex={nextOrderIndex}
          onSubmit={handleCreateCheckpoint}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}

      {/* Edit Form Modal */}
      {editingCheckpoint && (
        <CheckpointForm
          checkpoint={editingCheckpoint}
          onSubmit={handleUpdateCheckpoint}
          onCancel={() => setEditingCheckpoint(null)}
          loading={formLoading}
        />
      )}

      {/* QR Modal */}
      {qrModalData && (
        <CheckpointQRModal
          qrData={qrModalData.qrData}
          location={qrModalData.checkpoint.location}
          orderIndex={qrModalData.checkpoint.orderIndex}
          onClose={() => setQrModalData(null)}
        />
      )}
    </div>
  );
};
